import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { InteractiveRenderer } from '@aurora/interactives'
import { changeIdeas, engineProgress, gameStatus, latestVersions, pathStepHtml, playedLabel, versionAsk, versionLabel, waitingChanges } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import {
  activeChild,
  childMemory,
  gameTitle,
  gameVersions,
  isAgentThinking,
  materialKind,
  messagesFor,
  threadAbout,
} from '../store/selectors'
import { clock, madeAt, makingNote, gameNoun, gameSkill, playGame, retryGame } from '../lib/games'
import { isPrintable, readSpec } from '../lib/specs'
import { formatRelative } from '../lib/dates'
import { useMakingElapsed } from '../hooks/useElapsed'
import { useResolvedTheme } from '../hooks/useTheme'
import { useMedia } from '../hooks/useMedia'
import { Message } from '../components/thread/Message'
import { Composer } from '../components/thread/Composer'
import { GamePageVersions } from '../components/thread/ArtifactChip'
import { SpecPreview } from '../components/playroom/SpecPreview'
import { GameFace, MakingFace, PlayIcon, RetryIcon } from '../components/playroom/GameRow'
import { useStepStanding } from '../components/playroom/usePath'
import { GameMenu } from '../components/playroom/GameMenu'
import '../components/playroom/playroom.css'

/**
 * One game's own page: play it, see what it is and where it came from, and
 * change it by talking to Sapling right here.
 *
 * A game is a chain of versions. The page shows the newest one that plays,
 * so while Sapling is making a change the old version stays one tap away,
 * and the new one takes its place the moment it is ready. The conversation
 * is an ordinary thread whose subject is this game: it lists in the Thread
 * tab with the others, and every message carries the game along so Sapling
 * always knows which one is meant.
 */
export function GameView() {
  const id = useAuroraStore((s: any) => s.ui.selectedArtifactId as string | undefined)
  const child = useAuroraStore(activeChild) as any
  const versions = useAuroraStore((s: any) => gameVersions(s, id)) as any[]
  const current = latestVersions(versions)[0]
  const newest = versions[versions.length - 1]
  const pending = newest && current && newest._id !== current._id && gameStatus(newest) !== 'ready' ? newest : undefined
  const thread = useAuroraStore((s: any) => threadAbout(s, versions.map((v) => v._id))) as any
  const messages = useAuroraStore((s: any) => messagesFor(s, thread?._id)) as any[]
  const thinking = useAuroraStore((s: any) => isAgentThinking(s, thread?._id) || awaitingReply(messagesFor(s, thread?._id)))
  const memory = useAuroraStore(childMemory)
  const block = useAuroraStore((s: any) => (current?.blockRef ? s.blocks[current.blockRef] : undefined)) as any
  const title = useAuroraStore((s: any) => (current ? gameTitle(s, current) : '')) as string
  const [trying, setTrying] = useState(() => !!(auroraStore.getState() as any).ui.gamePreview)
  const [printing, setPrinting] = useState(false)
  const [draft, setDraft] = useState('')
  const wide = useMedia('(min-width: 1100px)')
  // A built-in book is played from here only once her path opens it, the same rule everywhere it is listed.
  const step = useStepStanding(current)
  const starters = current ? changeIdeas(current, { book: gameNoun(current) === 'Book', name: child?.name }) : []

  // A game's page opens at its top, wherever the page it came from was scrolled.
  useLayoutEffect(() => { document.querySelector('.shell-main')?.scrollTo(0, 0) }, [id])
  // Each game opens closed, unless it was opened to read together.
  useEffect(() => { setTrying(!!(auroraStore.getState() as any).ui.gamePreview) }, [id])

  // A new version taking its place is news: bring the hero, where it now
  // plays, into view. Only a change of version while the page is open.
  const hero = useRef<HTMLElement>(null)
  const shownVersion = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (shownVersion.current && current && shownVersion.current !== current._id) {
      hero.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
    shownVersion.current = current?._id
  }, [current?._id])

  // Opening the preview brings the whole game into view, its answers
  // included: its top just under the hero's buttons, and the stage sized
  // (in CSS) to the height left below them.
  const preview = useRef<HTMLElement>(null)
  useEffect(() => {
    if (trying) preview.current?.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }, [trying])

  const back = () => (auroraStore.getState() as any).setView('playroom')

  if (!current || !child) {
    return (
      <div className="page gp">
        <BackLink onClick={back} />
        <div className="empty">
          <p className="empty-title">This game is not here</p>
          <p className="empty-body">It may have been removed, or it has not reached this device yet.</p>
        </div>
      </div>
    )
  }

  const status = gameStatus(current)
  const playable = pathStepHtml(current) ?? current.html
  const progress = typeof playable === 'string' ? engineProgress(playable, current.carry, memory) : null
  const printable = isPrintable(materialKind(current)) && readSpec(current).ok

  const send = (content: string, artifactIds?: string[]) => {
    ;(auroraStore.getState() as any).sendMessage({
      childId: child._id,
      threadId: thread?._id,
      content,
      artifactIds: [current._id, ...(artifactIds ?? [])],
      artifactId: current._id,
    })
  }

  const made = versions.filter((v) => gameStatus(v) === 'ready').length
  const locked = status === 'ready' && step?.state === 'locked'
  const when = status === 'ready' ? madeAt(current) : current.createdAt
  const meta = [
    when ? `${status === 'ready' ? 'Made' : 'Asked for'} ${formatRelative(when)}` : '',
    // Only finished versions count here; one being made is the banner's and the rail's to say.
    made > 1 ? `version ${current.version ?? versions.indexOf(current) + 1} of ${made}` : versions.length > 1 ? `version ${current.version ?? 1}` : '',
    current.playCount ? playedLabel(current) : '',
  ].filter(Boolean).join(' · ')

  const name: string = child.name ?? 'her'
  // A first version still being made has no name yet: the page says what is
  // happening, and what was asked for sits under it as a quote, not as a title.
  const firstMaking = status === 'making' && !current.supersedes

  // A game that never finished has nothing to change yet: retry it, or wait for it, first.
  const talk = (status === 'ready' || messages.length > 0) && (
    <section className="section gp-talk" aria-labelledby="gp-talk">
      <div className="section-head">
        <h2 className="section-title" id="gp-talk">Change it with Sapling</h2>
        {thread && (
          <button
            className="btn btn-quiet btn-sm"
            onClick={() => {
              const s = auroraStore.getState() as any
              s.selectThread(thread._id)
              s.setView('thread')
            }}
          >
            Open in Thread
          </button>
        )}
      </div>
      <GamePageVersions.Provider value={new Set(versions.map((v) => v._id))}>
        <Conversation messages={messages} thinking={thinking} />
      </GamePageVersions.Provider>

      {!messages.length && (
        <div className="gp-asks">
          {starters.map((a) => (
            <button key={a} className="chip" onClick={() => setDraft(a)}>{a}</button>
          ))}
        </div>
      )}
      <div className="gp-composer">
        <Composer
          onSend={send}
          childId={child._id}
          draft={[draft, setDraft]}
          placeholder="What should change?"
        />
      </div>
    </section>
  )
  // Previewed on a desk, the game and the way to change it share the screen:
  // the game fits the window's height on the left, the conversation runs
  // beside it under Versions, so a parent can play, see, and ask at once.
  const talkBeside = wide && trying && status === 'ready'

  return (
    <div className="page gp">
      <BackLink onClick={back} />

      <div className={`gp-layout${talkBeside ? ' is-previewing' : ''}`}>
        <div className="gp-main">
          <header ref={hero} className={`gp-hero is-${status} reveal`}>
            <span className="gp-thumb" aria-hidden="true">
              {status === 'making' ? <MakingFace artifact={current} /> : <GameFace artifact={current} />}
            </span>
            <div className="gp-head">
              <p className="gp-noun">{gameNoun(current)}{block ? ` · for ${block.title}` : ''}</p>
              <h1 className="gp-title">{firstMaking ? `Making a game for ${name}` : title}</h1>
              {firstMaking && !block && title && <p className="gp-asked">“{title}”</p>}
              {current.summary && status === 'ready' && <p className="gp-summary">{current.summary}</p>}
              {progress && <p className="gp-progress">{progress}</p>}
              {meta && <p className="gp-meta tnum">{meta}</p>}
            </div>

            {status === 'making' && <Making artifact={current} />}
            {status === 'failed' && (
              <div className="gp-failed">
                <p>{current.generation?.error ?? 'This one did not come back.'} Asking again usually works.</p>
                <button className="btn btn-primary" onClick={() => retryGame(current)}><RetryIcon /> Try again</button>
              </div>
            )}

            {locked && (
              <>
                <p className="gp-locked"><LockIcon /> {step?.unlockHint ?? 'Not open on her reading path yet'}</p>
                {/* Not hers to read alone yet, but always one to read with her: the preview is the parent's, and nothing in it counts on her path. */}
                <div className="gp-actions">
                  <button className="btn btn-ghost gp-try" aria-expanded={trying} onClick={() => setTrying((v) => !v)}>
                    {trying ? 'Close the book' : 'Read it together'}
                  </button>
                  <span className="gp-together-note">You hold it; it does not count on her path</span>
                </div>
              </>
            )}

            {status === 'ready' && !locked && (
              <div className="gp-actions">
                {/* Named by who plays: full screen for her, in the page for the parent. */}
                <button
                  className="btn btn-primary btn-lg gp-play"
                  title={`Full screen, locked, for ${name}`}
                  onClick={() => playGame(current._id, current.blockRef)}
                >
                  <PlayIcon /> Hand it to {name}
                </button>
                <button className="btn btn-ghost gp-try" aria-expanded={trying} title="Play it here yourself first" onClick={() => setTrying((v) => !v)}>
                  {trying ? 'Close preview' : 'Preview'}
                </button>
                <GameMenu versions={versions} onPrint={printable ? () => setPrinting(true) : undefined} onRemoved={back} />
              </div>
            )}
          </header>

          {pending && <Revision artifact={pending} />}

          {trying && status === 'ready' && (
            <section ref={preview} className="gp-preview reveal">
              <SpecPreview artifact={current} />
            </section>
          )}

          {/* While she plays it here on a desk, the conversation sits beside the game, under Versions. */}
          {!talkBeside && talk}
        </div>

        <aside className="gp-side">
          <Versions versions={versions} current={current} waiting={waitingChanges(pending ?? (status === 'making' ? current : undefined))} />
          {talkBeside && talk}
        </aside>
      </div>

      {printing && <PrintSheet artifactId={current._id} onDone={() => setPrinting(false)} />}
    </div>
  )
}

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
)

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button className="gp-back" onClick={onClick}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      Playroom
    </button>
  )
}

/**
 * Honest progress for a first version: the words asked for, a bar with no
 * invented percentage, and the real seconds. Closing the page cancels
 * nothing; the game lands in the Playroom when it is done.
 */
function Making({ artifact }: { artifact: any }) {
  const elapsed = useMakingElapsed(artifact, true)
  const waiting = waitingChanges(artifact)
  return (
    <div className="gp-making" aria-live="polite">
      <div className="gen-bar" aria-hidden="true"><span className="gen-bar-sweep" /></div>
      <p className="gp-making-line tnum">
        Making it · {clock(elapsed)} · {makingNote(elapsed)}
      </p>
      {waiting.length > 0 && (
        <p className="gp-revision-then">
          <span className="gp-revision-then-k">Then, as version {(artifact.version ?? 1) + 1}</span> {waiting.join(' · ')}
        </p>
      )}
      <p className="gp-making-hint">You can leave this page. It keeps going and lands in the Playroom when it is ready.</p>
    </div>
  )
}

/** A change on its way (or one that did not come back), under the version that still plays. */
function Revision({ artifact }: { artifact: any }) {
  const label = versionLabel(artifact)
  const status = gameStatus(artifact)
  // Changes asked for while this one is being made: they wait, and become the version after it together.
  const waiting = status === 'making' ? waitingChanges(artifact) : []
  const elapsed = useMakingElapsed(artifact, status === 'making')
  return (
    <aside className={`gp-revision is-${status} reveal`} aria-live="polite">
      {status === 'making' && <div className="gen-bar" aria-hidden="true"><span className="gen-bar-sweep" /></div>}
      <p className="gp-revision-head tnum">
        {status === 'making' ? `Making version ${artifact.version ?? 'next'} · ${clock(elapsed)}` : `Version ${artifact.version ?? 'next'} did not come back`}
      </p>
      {label && <p className="gp-revision-prompt">{label}</p>}
      {waiting.length > 0 && (
        <p className="gp-revision-then">
          <span className="gp-revision-then-k">Then, as version {(artifact.version ?? 1) + 1}</span> {waiting.join(' · ')}
        </p>
      )}
      <p className="gp-revision-note">
        {status === 'making'
          ? 'The version above still plays. This one takes its place when it is ready.'
          : artifact.generation?.error ?? 'Nothing changed; the version above still plays.'}
      </p>
      {status === 'failed' && (
        <button className="btn btn-ghost btn-sm" onClick={() => retryGame(artifact)}><RetryIcon /> Try the change again</button>
      )}
    </aside>
  )
}

/** How many lines of the conversation show before "earlier": the latest exchange or two. */
const RECENT = 6

/**
 * The conversation, in the page's own flow: the latest few lines, with the
 * earlier ones a tap away, and the newest line brought into view when it
 * arrives (a reply, or Sapling's card for a new version).
 */
function Conversation({ messages, thinking }: { messages: any[]; thinking: boolean }) {
  const [all, setAll] = useState(false)
  // Where the shown lines start is fixed when the page opens, so a new line
  // adds below and never folds an earlier one away.
  const [from] = useState(() => messages[Math.max(0, messages.length - RECENT)]?._id as string | undefined)
  const end = useRef<HTMLDivElement>(null)
  const last = messages[messages.length - 1]?.content
  const first = useRef(true)
  useLayoutEffect(() => {
    // The page opens at the top; only a new line scrolls it. Held beside
    // the preview, the conversation scrolls in its own box, which opens at
    // its newest line without moving the page.
    if (first.current) {
      first.current = false
      const box = end.current?.parentElement
      if (box && box.scrollHeight > box.clientHeight) box.scrollTop = box.scrollHeight
      return
    }
    end.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [messages.length, last, thinking])
  if (!messages.length) {
    return (
      <p className="gp-talk-empty">
        Tell Sapling what to change, in your own words. It makes a new version and this page shows it the moment it is ready.
      </p>
    )
  }
  const hidden = all || !from ? 0 : Math.max(0, messages.findIndex((m) => m._id === from))
  return (
    <div className="gp-thread">
      {hidden > 0 && (
        <button className="gp-earlier" onClick={() => setAll(true)}>
          Earlier in this conversation · {hidden} more
        </button>
      )}
      {messages.slice(hidden).filter((m) => !blank(m)).map((m, i, shown) => <Message key={m._id} message={m} isLast={i === shown.length - 1} />)}
      {thinking && !messages.some((m) => m.streaming && !blank(m)) && (
        <p className="gp-working" role="status"><span className="gp-working-dot" aria-hidden="true" /> Sapling is on it</p>
      )}
      <div ref={end} />
    </div>
  )
}

/** A reply that has started but has nothing to show yet: the working row stands in for it. */
const blank = (m: any) => m.streaming && !m.content && !m.toolCalls?.length

/**
 * Every version, newest first, each with the change that made it and a way
 * to play it. A game with one version still keeps its place here, so the
 * page does not change shape the first time it is changed. The banner under
 * the hero is the one live status of a change; here a version being made
 * only pulses.
 */
function Versions({ versions, current, waiting }: { versions: any[]; current: any; waiting: string[] }) {
  const skill = gameSkill(current)
  const after = Math.max(...versions.map((v) => v.version ?? 1))
  return (
    <section className="section" aria-labelledby="gp-versions">
      <div className="section-head">
        <h2 className="section-title" id="gp-versions">Versions</h2>
      </div>
      <ol className="gp-versions">
        {waiting.length > 0 && (
          <li className="gp-version is-waiting">
            <span className="gp-version-n tnum">{after + 1}</span>
            <span className="gp-version-text">
              <span className="gp-version-ask">{waiting.join(' · ')}</span>
              <span className="gp-version-meta">Waiting · starts when version {after} is ready</span>
            </span>
          </li>
        )}
        {[...versions].reverse().map((v, i) => {
          const st = gameStatus(v)
          const n = v.version ?? versions.length - i
          return (
            <li key={v._id} className={`gp-version is-${st}${v._id === current._id ? ' is-current' : ''}`}>
              <span className="gp-version-n tnum">{n}</span>
              {(() => {
                // The status leads: a version still being made never reads as
                // the one playing, even while it is the only one there is.
                const label = versionLabel(v) ?? (v.supersedes ? 'A change' : 'The first version')
                const state = st === 'making' ? 'Being made' : st === 'failed' ? 'Did not come back' : v._id === current._id ? 'Plays now' : 'Earlier version'
                const when = st === 'ready' && madeAt(v) ? ` · made ${formatRelative(madeAt(v)!)}` : ''
                const ask = versionAsk(v)
                const head = (
                  <>
                    <span className="gp-version-ask">{label}</span>
                    <span className="gp-version-meta tnum">{state}{when}</span>
                  </>
                )
                // The exact request sent to the generator opens from the row itself.
                return ask ? (
                  <details className="gp-version-text gp-version-brief">
                    <summary aria-label={`${label}. ${state}. Show what Sapling asked for`}>{head}</summary>
                    <p><span className="gp-version-brief-k">What Sapling asked for</span>{ask}</p>
                  </details>
                ) : (
                  <span className="gp-version-text">{head}</span>
                )
              })()}
              {st === 'ready' && v._id !== current._id && (
                <button className="btn btn-quiet btn-sm" onClick={() => playGame(v._id)}><PlayIcon /> Play</button>
              )}
            </li>
          )
        })}
      </ol>
      {versions.length === 1 && gameStatus(current) === 'ready' && (
        <p className="gp-versions-note">
          {skill ? `Practises ${skill.toLowerCase()}. ` : ''}Each change you ask for becomes a new version here, and this one keeps playing until it is ready.
        </p>
      )}
    </section>
  )
}

/**
 * The parent has spoken and Sapling has not answered yet. The reply's row
 * lands a moment after the message does, so this bridges that moment; a
 * message long unanswered is not a reply still coming.
 */
function awaitingReply(messages: any[]): boolean {
  const last = messages[messages.length - 1]
  return last?.role === 'user' && Date.now() - (last.createdAt ?? 0) < 90_000
}

/**
 * Printing. The same renderer draws the sheet, so what comes out of the
 * printer is the activity itself, not a second paper version to drift.
 */
function PrintSheet({ artifactId, onDone }: { artifactId: string; onDone: () => void }) {
  const artifact = useAuroraStore((s: any) => s.artifacts[artifactId])
  const theme = useResolvedTheme()
  const read = readSpec(artifact)

  useEffect(() => {
    if (!read.ok) {
      onDone()
      return
    }
    const after = () => onDone()
    window.addEventListener('afterprint', after)
    // One frame for the renderer to lay out before the dialog freezes it.
    const id = window.setTimeout(() => window.print(), 120)
    return () => {
      window.clearTimeout(id)
      window.removeEventListener('afterprint', after)
    }
  }, [read.ok, onDone])

  if (!read.ok) return null
  return (
    <div className="print-sheet">
      <h1 className="print-sheet-title">{read.spec.title}</h1>
      <InteractiveRenderer spec={read.spec} theme={theme === 'dark' ? 'light' : theme} onResult={() => {}} />
    </div>
  )
}
